import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { default as useBreakpoint } from "@pb/utils/reactHooks/useBreakpoint";
import { default as getCurrentBreakpoint } from "@pb/utils/getCurrentBreakpoint";
import { default as IS_CLIENT } from "@pb/utils/constants/isClient";
import { isMonkiBrand } from "@pb/utils/brandConfig";
import cn from 'classnames';
import Slider from 'react-slick';
import { Button } from "../CTA";
import BodyText from "../Typography/texts/BodyText";
import styles from './Carousel.module.scss';
import CarouselControl from './components/CarouselControl';
import CarouselHeading from './components/CarouselHeading';
import { ControlType } from './constants';
const Carousel = forwardRef(({ children, slidesToShow = null, numOfItemsPerSlide, forceNumOfItemsPerSlide = null, labels, arrowClassName, arrowIconClassName, onAfterChange, onBeforeChange, onlyDots, onlyArrows, disableArrowsAndDots = false, arrowIconType = 'arrows-left-right', iconSizeNormal = false, verticalSlide = false, isInfinite = true, showDotsWithArrows = false, arrowsToTopRight = false, titleClassName, title, bleedOut = false, hasEqualHeightSlides = false, centerMode = false, centerPadding, rows = 1, slidesPerRow = 1, slickClassName, noPadding = false, isTitleCenterAligned = false, initialSlide = 0, slidesToScroll, lazyLoad, sliderKey = null, isDotsOnTop = false, variant = ControlType.ARROW, shouldShowSlideCount = false, arrowPosition = 'default', arrowBackground = 'default', arrowBackgroundSize = 'default', shouldProtectSliderClick = true, titleSize = 'medium', hasAdaptiveHeight = false, dotClassName, shouldRenderEmptySlides = true, isArrowAlignedWithNoPadding = false, labelControlClassName = '', enablePrevArrowOnLoad = false, shouldInitWithSingleBreakpoint = false, HeadingTag, onlyArrowsDesktop = false, shouldFirstAnimate, carouselHeadingClassName, }, ref) => {
    var _a;
    const breakpointState = useBreakpoint();
    const isMediumScreenOrSmaller = breakpointState === 'large' ||
        breakpointState === 'medium' ||
        breakpointState === 'small' ||
        breakpointState === 'xsmall';
    const isLargerThanMediumScreen = !isMediumScreenOrSmaller;
    const [activeIndex, setActiveIndex] = useState(0);
    const [clickableItemIndexes, setClickableItemIndexes] = useState([]);
    const [isSliding, setIsSliding] = useState(false);
    let protectiveSliderTimeout;
    const childrenArray = React.Children.toArray(children);
    const numOfItems = childrenArray.length;
    // TODO: fix having window object in SSR pages, this is a temporary solution
    const currentNumOfItemsPerSlide = forceNumOfItemsPerSlide !== null && forceNumOfItemsPerSlide !== void 0 ? forceNumOfItemsPerSlide : numOfItemsPerSlide[breakpointState];
    const numOfEmptySlides = currentNumOfItemsPerSlide - numOfItems;
    const carouselRef = useRef(null);
    const [disableNextArrow, setDisableNextArrow] = useState(false);
    const [disablePrevArrow, setDisablePrevArrow] = useState(!isInfinite && !enablePrevArrowOnLoad);
    let hasArrows = true;
    let hasDots = true;
    if (disableArrowsAndDots) {
        hasDots = false;
        hasArrows = false;
    }
    else if (onlyDots) {
        hasDots = true;
        hasArrows = false;
    }
    else if (onlyArrows) {
        hasDots = false;
        hasArrows = true;
    }
    else if (onlyArrowsDesktop) {
        hasDots = false;
        hasArrows = variant === ControlType.LABEL || isLargerThanMediumScreen;
    }
    else {
        hasDots = (isMediumScreenOrSmaller && variant !== ControlType.LABEL) || showDotsWithArrows;
        hasArrows = variant === ControlType.LABEL || isLargerThanMediumScreen;
    }
    // All visually hidden focus elements (defined by aria-hidden) should have tabindex of -1 in order to avoid accessibility failures.
    const updateFocusableElementTabIndex = () => {
        var _a, _b, _c;
        const slideTrack = (_c = (_b = (_a = carouselRef === null || carouselRef === void 0 ? void 0 : carouselRef.current) === null || _a === void 0 ? void 0 : _a.innerSlider) === null || _b === void 0 ? void 0 : _b.list) === null || _c === void 0 ? void 0 : _c.querySelector('.slick-track');
        const slides = slideTrack === null || slideTrack === void 0 ? void 0 : slideTrack.querySelectorAll('.slick-slide');
        slides === null || slides === void 0 ? void 0 : slides.forEach((slide) => {
            const isVisible = clickableItemIndexes.includes(Number(slide.dataset.index));
            if (isVisible) {
                slide.setAttribute('aria-hidden', 'false');
            }
            else {
                slide.setAttribute('aria-hidden', 'true');
            }
            const focusableElements = slide.querySelectorAll('a, button,[role="button"]');
            focusableElements.forEach((focusElement) => {
                focusElement.setAttribute('tabindex', isVisible ? '0' : '-1');
            });
        });
    };
    useEffect(() => {
        const allItemIndexes = childrenArray.map((child, childIndex) => childIndex);
        let newItemIndexes = allItemIndexes;
        if (allItemIndexes.length > currentNumOfItemsPerSlide) {
            // More posts than present in one slide
            if (activeIndex + currentNumOfItemsPerSlide > allItemIndexes.length) {
                // Last slide before restart. Will take one slide number of items from the end.
                const endIndex = allItemIndexes.length;
                const startIndex = endIndex - currentNumOfItemsPerSlide;
                newItemIndexes = allItemIndexes.slice(startIndex, endIndex);
            }
            else {
                const startIndex = activeIndex;
                const endIndex = startIndex + currentNumOfItemsPerSlide;
                newItemIndexes = allItemIndexes.slice(startIndex, endIndex);
            }
        }
        setClickableItemIndexes(newItemIndexes);
    }, [activeIndex, numOfItems, currentNumOfItemsPerSlide]);
    useEffect(() => {
        if (carouselRef.current) {
            clearTimeout(protectiveSliderTimeout);
            protectiveSliderTimeout = window.setTimeout(() => {
                updateFocusableElementTabIndex();
            }, 500);
        }
    }, [carouselRef.current, clickableItemIndexes]);
    const handleBeforeChange = (oldIndex, newIndex) => {
        if (shouldProtectSliderClick) {
            setIsSliding(true);
            protectiveSliderTimeout = window.setTimeout(() => {
                setIsSliding(false);
            }, 1000);
        }
        if (onBeforeChange) {
            onBeforeChange(oldIndex, newIndex);
        }
    };
    const updateArrowState = (newIndex) => {
        if (!isInfinite && children) {
            const amountOfCarouselItems = React.Children.count(children);
            if (newIndex === 0) {
                setDisablePrevArrow(true);
                if (disableNextArrow) {
                    setDisableNextArrow(false);
                }
            }
            else if (newIndex <= amountOfCarouselItems &&
                newIndex >= amountOfCarouselItems - currentNumOfItemsPerSlide) {
                setDisableNextArrow(true);
                if (disablePrevArrow) {
                    setDisablePrevArrow(false);
                }
            }
            else {
                if (disableNextArrow) {
                    setDisableNextArrow(false);
                }
                if (disablePrevArrow) {
                    setDisablePrevArrow(false);
                }
            }
        }
    };
    const handleAfterChange = (newIndex) => {
        setIsSliding(false);
        clearTimeout(protectiveSliderTimeout);
        setActiveIndex(newIndex);
        updateArrowState(newIndex);
        if (onAfterChange) {
            onAfterChange(newIndex);
        }
    };
    // Adding check to prevent rendering before breakpoint state is actually currently used breakpoint, only for client
    if (numOfItems < 1 ||
        (IS_CLIENT && breakpointState !== getCurrentBreakpoint() && shouldInitWithSingleBreakpoint)) {
        return null;
    }
    const sliderRef = ref || carouselRef;
    const activeRowNumber = Math.ceil(activeIndex / currentNumOfItemsPerSlide + 1);
    const maxNumberOfRows = Math.ceil(numOfItems / (currentNumOfItemsPerSlide * slidesPerRow));
    return (React.createElement(React.Fragment, null,
        React.createElement(CarouselHeading, { title: title, titleClassName: titleClassName, isLargerThanMediumScreen: isLargerThanMediumScreen, labels: labels, arrowsToTopRight: arrowsToTopRight, carouselRef: sliderRef, disablePrevArrow: disablePrevArrow, disableNextArrow: disableNextArrow, hasPadding: !verticalSlide && !noPadding && (onlyArrows || isLargerThanMediumScreen), isTitleCenterAligned: isTitleCenterAligned, titleSize: titleSize, HeadingTag: HeadingTag, className: carouselHeadingClassName }),
        React.createElement(Slider, Object.assign({ lazyLoad: lazyLoad, ref: sliderRef, className: cn(styles.slider, noPadding && styles.noPadding, noPadding && isArrowAlignedWithNoPadding && styles.isArrowAlignedWithNoPadding, { [styles.dotsSlider]: hasDots }, { [styles.arrowSlider]: hasArrows && variant === ControlType.ARROW }, { [styles.labelSlider]: hasArrows && variant === ControlType.LABEL }, { [styles.verticalSlider]: verticalSlide }, { [styles.bleedOut]: bleedOut }, { [styles.equalHeightSlides]: hasEqualHeightSlides }, slickClassName, shouldFirstAnimate && styles.slideFirstTime, { [styles.dotsOnTop]: isDotsOnTop }), slidesToShow: slidesToShow !== null && slidesToShow !== void 0 ? slidesToShow : currentNumOfItemsPerSlide, slidesToScroll: slidesToScroll || isMonkiBrand
                ? currentNumOfItemsPerSlide / currentNumOfItemsPerSlide
                : currentNumOfItemsPerSlide, adaptiveHeight: hasAdaptiveHeight, accessibility: true, infinite: numOfEmptySlides > 0 ? false : isInfinite, beforeChange: handleBeforeChange, afterChange: handleAfterChange, dots: hasDots, dotsClass: cn(isDotsOnTop ? 'slick-dots-above' : 'slick-dots', dotClassName), customPaging: (index) => (React.createElement(Button, { className: styles.dots }, index ? `${index + 1}` : '1')), arrows: hasArrows && !arrowsToTopRight, vertical: verticalSlide, rtl: ((_a = process.env.NEXT_PUBLIC_BODY_ALIGNMENT) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'rtl', nextArrow: React.createElement(CarouselControl, { direction: "next", label: labels.next, disabled: disableNextArrow || arrowsToTopRight, iconSizeNormal: iconSizeNormal, arrowIconType: arrowIconType, arrowClassName: arrowClassName, arrowIconClassName: arrowIconClassName, variant: variant, arrowBackground: arrowBackground, arrowPosition: arrowPosition, arrowBackgroundSize: arrowBackgroundSize, labelControlClassName: labelControlClassName }), prevArrow: React.createElement(CarouselControl, { direction: "prev", label: labels.prev, disabled: disablePrevArrow || arrowsToTopRight, iconSizeNormal: iconSizeNormal, arrowIconType: arrowIconType, arrowClassName: arrowClassName, arrowIconClassName: arrowIconClassName, variant: variant, arrowBackground: arrowBackground, arrowPosition: arrowPosition, arrowBackgroundSize: arrowBackgroundSize, labelControlClassName: labelControlClassName }), centerMode: centerMode, rows: rows, slidesPerRow: slidesPerRow }, (centerPadding && { centerPadding }), { initialSlide: initialSlide, key: sliderKey }),
            shouldProtectSliderClick
                ? React.Children.toArray(children).map((child, childIndex) => {
                    return React.cloneElement(child, {
                        carouselIsSliding: isSliding,
                        clickable: clickableItemIndexes.indexOf(childIndex) > -1,
                    });
                })
                : children,
            shouldRenderEmptySlides &&
                numOfEmptySlides > 0 &&
                Array(numOfEmptySlides)
                    .fill(0, 0, numOfEmptySlides)
                    .map((i) => (React.createElement("div", { key: i }))) /* When less items in Carousel then one slide can hold, we need this to align items  to the left. */),
        shouldShowSlideCount && (React.createElement("div", { className: styles.slidesCountWrapper },
            React.createElement(BodyText, { Tag: "span", className: styles.slideCount }, `${activeRowNumber <= maxNumberOfRows ? activeRowNumber : maxNumberOfRows}/${maxNumberOfRows}`)))));
});
export default Carousel;
//# sourceMappingURL=Carousel.js.map